import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { KccLineChart, KccBarChart, KccAreaChart } from "@/components/kcc/Charts";

const IMG = "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?crop=entropy&cs=srgb&fm=jpg&auto=compress&w=940&h=650";

export default function Finance360() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/finance/overview").then(({ data }) => setData(data)); }, []);
    const ledger = (data?.ledger || []).slice(-18).map((l) => ({
        date: l.period.slice(2),
        revenue: l.revenue_musd,
        ebitda: l.ebitda_musd,
        ni: l.net_income_musd,
        opex: l.opex_musd,
        capex: l.capex_musd,
        cu_cost: l.cash_cost_cu_usd_lb,
        co_cost: l.cash_cost_co_usd_lb,
        fx: l.fx_usd_cdf,
    }));

    const opex = data?.latest?.opex_breakdown
        ? Object.entries(data.latest.opex_breakdown).map(([k, v]) => ({ name: k, value: v }))
        : [];

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Finance360 · Executive"
                title="Chief Financial Officer Command Centre"
                description="Revenue, EBITDA, cash cost per lb of copper & cobalt, budget vs actual by cost centre, FX exposure, working capital, fraud analytics and executive financial risk."
                image={IMG}
            />
            <KpiGrid kpis={data?.kpis || []} accent="cobalt" cols={4} />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Revenue vs EBITDA vs Net Income" subtitle="18-month monthly · US$ M" accent="cobalt" className="xl:col-span-2">
                    <KccLineChart
                        data={ledger}
                        series={[
                            { key: "revenue", label: "Revenue", color: "#4f8bd9" },
                            { key: "ebitda", label: "EBITDA", color: "#e88c4a" },
                            { key: "ni", label: "Net Income", color: "#22c55e" },
                        ]}
                        height={320}
                    />
                </Panel>
                <Panel title="OPEX Breakdown" subtitle="Latest month · US$ M" accent="copper">
                    <KccBarChart data={opex} dataKey="value" label="US$ M" color="#e88c4a" xKey="name" height={320} />
                </Panel>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Cash Cost of Copper" subtitle="US$ / lb Cu · month" accent="copper">
                    <KccAreaChart data={ledger} dataKey="cu_cost" label="Cu $/lb" color="#e88c4a" height={220} />
                </Panel>
                <Panel title="Cash Cost of Cobalt" subtitle="US$ / lb Co · month" accent="cobalt">
                    <KccAreaChart data={ledger} dataKey="co_cost" label="Co $/lb" color="#4f8bd9" height={220} />
                </Panel>
                <Panel title="FX Exposure (USD ↔ CDF)" subtitle="Monthly mid-rate" accent="purple">
                    <KccAreaChart data={ledger} dataKey="fx" label="CDF" color="#8b5cf6" height={220} />
                </Panel>
            </div>

            <Panel title="Cost Centre Performance" subtitle="Budget vs actual · variance highlighted" accent="cobalt">
                <DataTable
                    columns={[
                        { key: "cost_centre", label: "Cost Centre" },
                        { key: "budget_musd", label: "Budget $M" },
                        { key: "actual_musd", label: "Actual $M" },
                        { key: "variance_musd", label: "Variance $M", render: (r) => <span className={r.variance_musd > 0 ? "text-kcc-red" : "text-kcc-green"}>{r.variance_musd > 0 ? "+" : ""}{r.variance_musd}</span> },
                        { key: "variance_pct", label: "Variance %", render: (r) => `${r.variance_pct}%` },
                        { key: "ytd_forecast_musd", label: "YTD Forecast $M" },
                        { key: "owner", label: "Owner" },
                    ]}
                    rows={data?.cost_centres || []}
                    testid="finance-costcentres"
                />
            </Panel>

            <Panel title="Fraud Analytics — Indicator Register" subtitle="Auto-detected anomalies" accent="red">
                <DataTable
                    columns={[
                        { key: "ref", label: "Ref" },
                        { key: "detected", label: "Detected", render: (r) => r.detected.slice(0, 10) },
                        { key: "type", label: "Indicator" },
                        { key: "severity", label: "Severity", render: (r) => <StatusChip status={statusToChip(r.severity)}>{r.severity}</StatusChip> },
                        { key: "amount_usd", label: "Amount", render: (r) => `$${r.amount_usd.toLocaleString()}` },
                        { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        { key: "owner", label: "Owner" },
                    ]}
                    rows={data?.fraud || []}
                    testid="finance-fraud"
                />
            </Panel>
        </div>
    );
}
